/**
 * iter150AZ.83 — Generic confirmation dialog.
 *
 * Drop-in replacement for the browser's ``window.confirm`` that
 * supports branded styling, multi-line message, distinct OK/Cancel
 * labels, and a "destructive" variant for delete-style actions
 * (rose OK button instead of blue).
 *
 * Usage (imperative — the hook returns ``[confirm, dialog]``):
 *
 *   const [confirm, dialog] = useConfirm();
 *   <... onClick={async () => {
 *     if (await confirm({
 *       title: "Delete all windows on MON?",
 *       message: "This removes 4 stop windows. You can re-add them.",
 *       confirmLabel: "Delete all",
 *       destructive: true,
 *     })) { doDelete(); }
 *   }}>
 *   {dialog}
 */
import { useCallback, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Warning } from "@phosphor-icons/react";


export function useConfirm() {
  const [state, setState] = useState({
    open:         false,
    title:        "",
    message:      "",
    confirmLabel: "OK",
    cancelLabel:  "Cancel",
    destructive:  false,
    resolver:     null,
  });

  const confirm = useCallback((opts = {}) => {
    return new Promise((resolve) => {
      setState({
        open:         true,
        title:        opts.title        || "Are you sure?",
        message:      opts.message      || "",
        confirmLabel: opts.confirmLabel || "OK",
        cancelLabel:  opts.cancelLabel  || "Cancel",
        destructive:  !!opts.destructive,
        resolver:     resolve,
      });
    });
  }, []);

  function close(answer) {
    const r = state.resolver;
    setState((s) => ({ ...s, open: false, resolver: null }));
    r?.(answer);
  }

  const dialog = (
    <Dialog open={state.open} onOpenChange={(o) => { if (!o) close(false); }}>
      <DialogContent className="sm:max-w-md" data-testid="confirm-dialog">
        <DialogHeader>
          <DialogTitle className="text-base font-bold flex items-center gap-2">
            {state.destructive && <Warning size={18} weight="fill" className="text-rose-600" />}
            {state.title}
          </DialogTitle>
          {state.message && (
            <DialogDescription
              className="text-xs text-slate-600 whitespace-pre-line"
              data-testid="confirm-dialog-message">
              {state.message}
            </DialogDescription>
          )}
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <button type="button"
                  onClick={() => close(false)}
                  data-testid="confirm-dialog-cancel"
                  className="text-xs px-3 py-1.5 rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50">
            {state.cancelLabel}
          </button>
          <button type="button"
                  onClick={() => close(true)}
                  data-testid="confirm-dialog-confirm"
                  className={`text-xs font-bold px-3 py-1.5 rounded-sm text-white ${
                    state.destructive ? "bg-rose-700 hover:bg-rose-800" : "bg-blue-700 hover:bg-blue-800"
                  }`}>
            {state.confirmLabel}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return [confirm, dialog];
}
